/**
 * Typed wrappers around the Kai-API Curate routes (`/curate/*`).
 *
 * Curate is the personalized-training surface: a provider is assigned articles
 * authored in Kai-Studio, reads them page by page, answers any embedded
 * question blocks, and completes them. Everything here is scoped server-side to
 * the caller's own NPI — the client never sends an NPI, and cannot widen the
 * view by asking for one.
 *
 * Two failure modes are normal rather than exceptional, and callers should
 * expect both:
 *
 *   - `503 CURATE_NOT_ENABLED` — the tenant has no Curate tables (not migrated,
 *     or an environment not on Lakebase). Probe `getCurateStatus()` first; it
 *     answers 200 with `enabled: false` precisely so the UI can hide the
 *     feature instead of rendering an error.
 *   - `403 NPI_NOT_CONFIGURED` — the signed-in account has no NPI claim, so it
 *     has no assignments by definition.
 *
 * Shapes mirror `Kai-API/src/services/CurateService.ts`. Keep them in sync.
 */

import { apiGet, apiPost, request } from './client';

/* Enums — mirror CurateService
 * -------------------------------------------------------------------------- */

export type AssignmentState = 'new' | 'in_progress' | 'completed' | 'expired';

export type AssignmentReason =
  | 'manual'
  | 'indicator_deficit'
  | 'complaint_deficit'
  | 'tag_rule';

export type ResponseType = 'single_choice' | 'multi_select' | 'free_text';

export type BlockType =
  | 'heading'
  | 'rich_text'
  | 'bullet_list'
  | 'numbered_list'
  | 'image'
  | 'quote'
  | 'callout'
  | 'divider'
  | 'page_break'
  | 'question';

export type CurateDisabledReason = 'lakebase_not_configured' | 'tables_missing';

/* Availability
 * -------------------------------------------------------------------------- */

export interface CurateAvailability {
  enabled: boolean;
  reason?: CurateDisabledReason;
  /** Tables the probe expected but did not find. Empty when enabled. */
  missingTables: string[];
  checkedAt: string;
}

/**
 * Is Curate usable for this tenant? Always resolves (200) rather than throwing
 * when the feature is off, so the page can branch on `enabled` instead of
 * treating a normal state as an error.
 */
export const getCurateStatus = (signal?: AbortSignal) =>
  apiGet<CurateAvailability>('/curate/status', { signal });

/* Articles, blocks, assignments
 * -------------------------------------------------------------------------- */

/** The article fields the learner surface receives alongside an assignment. */
export interface AssignmentArticle {
  slug: string;
  title: string;
  subtitle: string | null;
  summary: string | null;
  headerImageUrl: string | null;
  tags: string[];
  pageCount: number;
  questionCount: number;
}

export interface CurateProgress {
  assignmentId: string;
  providerNpi: string;
  /** Resume position: the page the reader reopens on. */
  lastPageIndex: number;
  /** High-water mark, so backtracking never reduces progress. */
  furthestPageIndex: number;
  pagesViewed: number;
  openedCount: number;
  totalSeconds: number;
  lastOpenedAt: string | null;
  lastInteractionAt: string | null;
  closedAt: string | null;
}

export interface CurateAssignment {
  assignmentId: string;
  articleId: string;
  articleVersion: number;
  providerNpi: string;
  assignedBy: string | null;
  reason: AssignmentReason;
  sourceIndicatorId: string | null;
  sourceComplaintId: string | null;
  sourceTag: string | null;
  deficitScore: number | null;
  goal: number | null;
  state: AssignmentState;
  /** 0-100, or null when the article has no graded questions. No pass/fail. */
  score: number | null;
  dueDate: string | null;
  assignedAt: string | null;
  startedAt: string | null;
  completedAt: string | null;
}

export interface AssignmentWithArticle extends CurateAssignment {
  article: AssignmentArticle;
  progress: CurateProgress | null;
  /** 0-100, derived server-side from pages viewed over page count. */
  progressPct: number;
}

/**
 * One content block, carrying the Kai-Studio payload unchanged. `payload` is
 * intentionally loose here: its shape varies by `blockType`, and the renderer
 * narrows it per type rather than this module guessing at a union that would
 * have to track Kai-Studio's format.
 */
export interface CurateBlock {
  blockId: string;
  articleId: string;
  version: number;
  position: number;
  /** Precomputed at import from preceding page_break blocks. */
  pageIndex: number;
  blockType: BlockType;
  isQuestion: boolean;
  payload: Record<string, unknown>;
}

export interface CurateArticleDetail {
  articleId: string;
  version: number;
  isCurrent: boolean;
  slug: string;
  title: string;
  subtitle: string | null;
  summary: string | null;
  status: string;
  headerImageUrl: string | null;
  tags: string[];
  blockCount: number;
  pageCount: number;
  questionCount: number;
  source: string;
  sourceExportId: string | null;
  authorEmail: string | null;
  publishedAt: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface CurateResponse {
  responseId: string;
  assignmentId: string;
  providerNpi: string;
  blockId: string;
  articleId: string;
  articleVersion: number;
  responseType: ResponseType;
  /** 0-based option indexes into the pinned payload. Null for free text. */
  selectedOptions: number[] | null;
  freeText: string | null;
  /** Server-side grading. Null for free text and ungraded questions. */
  isCorrect: boolean | null;
  attemptCount: number;
  firstAnsweredAt: string | null;
  answeredAt: string | null;
}

/** Everything the reader needs in one round trip. */
export interface AssignmentReader {
  assignment: CurateAssignment;
  article: CurateArticleDetail;
  blocks: CurateBlock[];
  progress: CurateProgress | null;
  responses: CurateResponse[];
}

/* Learner endpoints
 * -------------------------------------------------------------------------- */

export interface MyAssignmentsFilters {
  state?: AssignmentState;
  limit?: number;
  offset?: number;
}

export const getMyAssignments = (filters: MyAssignmentsFilters = {}, signal?: AbortSignal) =>
  apiGet<AssignmentWithArticle[]>('/curate/my/assignments', {
    query: { state: filters.state, limit: filters.limit, offset: filters.offset },
    signal,
  });

export const getAssignmentReader = (assignmentId: string, signal?: AbortSignal) =>
  apiGet<AssignmentReader>(`/curate/assignments/${assignmentId}/reader`, { signal });

/**
 * Record that the learner opened the article. Upserts the progress row,
 * increments the open counter, and moves a `new` assignment to `in_progress`.
 * Call once per reader open.
 */
export const openAssignment = (assignmentId: string) =>
  apiPost<CurateProgress>(`/curate/assignments/${assignmentId}/open`, {});

export interface SaveProgressBody {
  /** 0-based page the learner is now on. */
  pageIndex: number;
  /** Seconds to ADD to the running total, not an absolute value. */
  dwellSeconds?: number;
  /** True when the reader was dismissed. */
  closed?: boolean;
}

/**
 * Save reading position. Called on page transition and on close — deliberately
 * not continuously; the schema is designed for roughly one write per page turn
 * rather than a per-interaction event stream.
 */
export const saveProgress = (assignmentId: string, body: SaveProgressBody) =>
  apiPost<CurateProgress>(`/curate/assignments/${assignmentId}/progress`, body);

export interface SaveAnswerBody {
  /** 0-based option indexes. Use for single_choice and multi_select. */
  selectedOptions?: number[];
  /** Use for free_text. */
  freeText?: string;
}

/**
 * Upsert an answer to one question block. Re-answering updates the same row and
 * increments `attemptCount`. Grading happens server-side against the pinned
 * block payload — the client never sends or infers correctness.
 */
export const saveAnswer = (assignmentId: string, blockId: string, body: SaveAnswerBody) =>
  request<CurateResponse>(`/curate/assignments/${assignmentId}/responses/${blockId}`, {
    method: 'PUT',
    body,
  });

/** Clear a saved answer. Resolves to void (the API answers 204). */
export const clearAnswer = (assignmentId: string, blockId: string) =>
  request<void>(`/curate/assignments/${assignmentId}/responses/${blockId}`, {
    method: 'DELETE',
  });

/**
 * Mark the assignment complete. Score is computed server-side from graded
 * responses and stays null when the article has no graded questions.
 * Completion is never blocked by the score: there is no pass/fail.
 */
export const completeAssignment = (assignmentId: string) =>
  apiPost<CurateAssignment>(`/curate/assignments/${assignmentId}/complete`, {});
